import { Auth } from "@aws-amplify/auth"
import { createContext, Dispatch, FunctionComponent, SetStateAction, useEffect, useState } from "react"
import { Group } from "../data/models.interface"

interface IAppContext {
	isAuthenticated: boolean
	setIsAuthenticated: Dispatch<SetStateAction<boolean>>
	groups: Group[]
	setGroups: Dispatch<SetStateAction<Group[]>>
}

/** AppContext holds any global state that needs to be accessed throughout the application. */
const AppContext = createContext<IAppContext>({
	isAuthenticated: false,
	setIsAuthenticated: () => {},
	groups: [],
	setGroups: () => {},
})

interface IAppProvider {
	children?: JSX.Element | JSX.Element[]
}

const AppProvider: FunctionComponent<IAppProvider> = ({ children }) => {
	const [isAuthenticated, setIsAuthenticated] = useState(false)
	const [groups, setGroups] = useState<Group[]>([])
	/** Check if the user is already logged in when the application first loads.
	 * When a user logs in, their JWT token is stored in local storage.
	 * So if they refresh the page or leave and come back within the token's expiration time, this will see their token and set them as authenticated.
	 */
	useEffect(() => {
		Auth.currentSession()
			.then((session) => {
				setGroups(session.getIdToken().payload["cognito:groups"])
				setIsAuthenticated(true)
			})
			.catch(() => {
				setGroups([])
				setIsAuthenticated(false)
			})
	}, [setIsAuthenticated])
	const value: IAppContext = {
		isAuthenticated,
		setIsAuthenticated,
		groups,
		setGroups,
	}
	return <AppContext.Provider value={value}>{children}</AppContext.Provider>
}

export { AppContext, AppProvider }
